import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  FC,
} from 'react';
import {
  AgentMessage,
  AgentStreamBeginEvent,
  AgentStreamEvents,
  AgentStreamFinishEvent,
  ManagerCompleteEvent,
  AgentErrorEvent,
  ToolStartEvent,
  ToolEndEvent,
  GetThreadLiveStateResponse,
  LiveStateEvent,
} from '@dreamai/types';
import api from '../../../api';

// Define interfaces if they're not exported from @dreamai/types
interface TokenDelta {
  input?: string;
  [key: string]: any;
}

interface TokenEvent {
  token: string[] | string | TokenDelta | TokenDelta[];
  thread_id: string;
  timestamp: string;
  is_called_agent: boolean;
}

import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import { usePusher } from '../../../contexts/PusherContext';
import { useNewThread } from '../../../contexts/NewThreadContext';
import { useThreadLoading } from '../../../contexts/ThreadLoadingContext';

import {
  extractResponseFromPartialJson,
  extractMetadataFromPartialJson,
  normalizeMessageBreaksForDisplay,
} from './threadUtils';

import { ReactComponent as CircleInfoIcon } from '../../../icons/circle-info.svg';
import { ReactComponent as ErrorHeartbeatIcon } from '../../../icons/error-heartbeat.svg';

import ThinkingAnimation from './ThinkingAnimation';
import MultiModalContent from './MultiModalContent';

interface StreamHandlerProps {
  threadId?: string | null;
  messages: AgentMessage[];
  setMessages: (
    messages:
      | AgentMessage[]
      | ((prevMessages: AgentMessage[]) => AgentMessage[]),
  ) => void;
  onMessageSent?: (threadId: string, responseMessages?: AgentMessage[]) => void;
  onError?: (error: string | null) => void;
  onRecoverFromError?: () => void; // Callback when agent recovers from error
  onAbortSucceeded?: () => void; // Callback when abort succeeds
  predominantColour?: string;
  onScrollNeeded?: () => void;
  resetErrorOnNewMessage?: boolean; // If true, clear error state when messages change
  fetchThreadMessages?: (threadId: string) => Promise<void>; // Function to refresh thread messages
  messageWrapper?: React.ComponentType<{ children: React.ReactNode }>; // Optional wrapper component
  agentNickname?: string;
  onThinkingChange?: (isThinking: boolean) => void; // Callback when thinking state changes
}

const StreamHandlerComponent: FC<StreamHandlerProps> = ({
  threadId,
  setMessages,
  onMessageSent,
  onError,
  onRecoverFromError,
  onAbortSucceeded,
  predominantColour,
  onScrollNeeded,
  resetErrorOnNewMessage,
  fetchThreadMessages,
  messageWrapper: MessageWrapper,
  agentNickname,
  onThinkingChange,
}) => {
  // Internal state for streaming and thinking
  const [isThinking, setIsThinking] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamedContent, setStreamedContent] = useState('');
  const streamedContentRef = useRef('');
  const [streamedMetadata, setStreamedMetadata] = useState({});
  const streamedMetadataRef = useRef({});

  // Smooth streaming: buffer raw content, render incrementally
  const rawContentRef = useRef(''); // Full raw content received
  const renderedLengthRef = useRef(0); // How much of extracted response we've shown
  const streamIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const CHARS_PER_TICK = 3; // Characters to reveal per tick
  const TICK_MS = 16; // ~60fps
  const [hasError, setHasError] = useState(false);
  const [errorType, setErrorType] = useState<string | null>(null);
  const [willNeedToRefreshMessages, _setWillNeedToRefreshMessages] =
    useState(false);
  const willNeedToRefreshMessagesRef = useRef(false);
  // Wrapper that keeps both state and ref in sync
  const setWillNeedToRefreshMessages = useCallback((value: boolean) => {
    willNeedToRefreshMessagesRef.current = value;
    _setWillNeedToRefreshMessages(value);
  }, []);
  const [activeToolCalls, setActiveToolCalls] = useState<
    Record<string, ToolStartEvent>
  >({});
  const [thinkingTokens, setThinkingTokens] = useState('');
  const [liveStateInitialized, setLiveStateInitialized] = useState<
    string | null
  >(null); // Track which threadId we've initialized live state for
  // Track error cleanup state: which thread and when
  const errorCleanupRef = useRef<{
    threadId: string | null;
    timestamp: number;
  } | null>(null);
  const threadChannelRef = useRef<any>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { newThreadId } = useNewThread();
  const { getAccessTokenSilently } = useAuth0();
  const { subscribeToThreadChannel, unsubscribeFromThreadChannel } =
    usePusher(true);
  const {
    registerAgentStreamBeginCallback,
    unregisterAgentStreamBeginCallback,
    registerAgentStreamFinishCallback,
    unregisterAgentStreamFinishCallback,
    registerManagerCompleteCallback,
    unregisterManagerCompleteCallback,
    registerAgentErrorCallback,
    unregisterAgentErrorCallback,
    registerAbortSucceededCallback,
    unregisterAbortSucceededCallback,
    registerTokenCallback,
    unregisterTokenCallback,
    registerThinkingTokenCallback,
    unregisterThinkingTokenCallback,
    registerToolStartCallback,
    unregisterToolStartCallback,
    registerToolEndCallback,
    unregisterToolEndCallback,
    registerIndividualAgentStartCallback,
    unregisterIndividualAgentStartCallback,
    registerIndividualAgentFinishCallback,
    unregisterIndividualAgentFinishCallback,
    registerMessageEditedCallback,
    unregisterMessageEditedCallback,
  } = useThreadLoading();

  // Store Pusher functions in refs to prevent dependency changes
  const subscribeToThreadChannelRef = useRef(subscribeToThreadChannel);
  const unsubscribeFromThreadChannelRef = useRef(unsubscribeFromThreadChannel);

  // Update refs when functions change
  useEffect(() => {
    subscribeToThreadChannelRef.current = subscribeToThreadChannel;
    unsubscribeFromThreadChannelRef.current = unsubscribeFromThreadChannel;
  }, [subscribeToThreadChannel, unsubscribeFromThreadChannel]);

  // Memoize the thread ID to prevent unnecessary effect triggers
  const toUseThreadId = useMemo(
    () => threadId || newThreadId,
    [newThreadId, threadId],
  );

  // Effect to trigger scroll when streaming content or thinking tokens change
  useEffect(() => {
    if ((isThinking || streamedContent || thinkingTokens) && onScrollNeeded) {
      onScrollNeeded();
    }
  }, [isThinking, streamedContent, thinkingTokens, onScrollNeeded]);

  // Effect to notify parent when thinking state changes
  useEffect(() => {
    onThinkingChange?.(isThinking);
  }, [isThinking, onThinkingChange]);

  // Effect to clear error state when resetErrorOnNewMessage changes
  useEffect(() => {
    setHasError(false);
    setErrorType(null);
  }, [resetErrorOnNewMessage]);

  // Cleanup stream interval on unmount
  useEffect(() => {
    return () => {
      if (streamIntervalRef.current) {
        clearInterval(streamIntervalRef.current);
        streamIntervalRef.current = null;
      }
    };
  }, []);

  // Effect to fetch and replay live state when opening a thread mid-execution
  useEffect(() => {
    // Skip if no threadId or if we've already initialized for this thread
    if (!toUseThreadId || liveStateInitialized === toUseThreadId) {
      return;
    }

    const fetchLiveState = async () => {
      try {
        console.log(
          `StreamHandler: Fetching live state for thread ${toUseThreadId}`,
        );
        const token = await getAccessTokenSilently();
        const response = await api.get<GetThreadLiveStateResponse>(
          `/agents/threads/${toUseThreadId}/live-state`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const { hasLiveState, events } = response.data;

        if (hasLiveState && events.length > 0) {
          console.log(
            `StreamHandler: Found ${events.length} live state events, replaying...`,
          );

          // Set flag to refresh messages when agent completes
          setWillNeedToRefreshMessages(true);

          // Replay events in order to initialize state
          for (const event of events) {
            console.log(`StreamHandler: Replaying event: ${event.eventType}`);

            switch (event.eventType) {
              case AgentStreamEvents.MANAGER_BEGIN:
                setIsThinking(true);
                setHasError(false);
                setErrorType(null);
                break;

              case AgentStreamEvents.TOOL_START: {
                // Add tool to active tool calls
                const toolStartData = event.eventData as ToolStartEvent;
                setActiveToolCalls((prev) => ({
                  ...prev,
                  [toolStartData.tool]: toolStartData,
                }));
                // Also add tool message to chat
                const toolMessage: AgentMessage = {
                  role: 'tool',
                  content: toolStartData.tool,
                  tool_call_input: toolStartData.input || undefined,
                  tool_call_id: toolStartData.id,
                };
                setMessages((prev) => [...prev, toolMessage]);
                break;
              }

              case AgentStreamEvents.TOOL_END: {
                // Remove tool from active tool calls
                const toolEndData = event.eventData as ToolEndEvent;
                setActiveToolCalls((prev) => {
                  const newCalls = { ...prev };
                  delete newCalls[toolEndData.tool];
                  return newCalls;
                });
                break;
              }

              case AgentStreamEvents.AGENT_STREAM_BEGIN:
                setIsStreaming(true);
                setHasError(false);
                setErrorType(null);
                break;

              // Note: AGENT_STREAM_FINISH is not stored in live state
              // It triggers a clear instead, and the message comes from checkpoint
            }
          }
        }

        setLiveStateInitialized(toUseThreadId);
      } catch (error) {
        console.error('StreamHandler: Error fetching live state:', error);
        // Don't block on errors - just mark as initialized to prevent retries
        setLiveStateInitialized(toUseThreadId);
      }
    };

    fetchLiveState();
  }, [
    toUseThreadId,
    liveStateInitialized,
    setMessages,
    getAccessTokenSilently,
  ]);

  // Handler for abort succeeded events
  const handleAbortSucceeded = useCallback(
    (data: any) => {
      console.log('StreamHandler: Abort succeeded:', data);

      // Cancel any pending stream interval
      if (streamIntervalRef.current) {
        clearInterval(streamIntervalRef.current);
        streamIntervalRef.current = null;
      }

      // Clear streaming state
      setIsThinking(false);
      setIsStreaming(false);
      setStreamedContent('');
      setThinkingTokens('');
      streamedContentRef.current = '';
      rawContentRef.current = '';
      renderedLengthRef.current = 0;

      // Note: Loading state is now managed by ThreadLoadingContext

      // Call the onAbortSucceeded callback if provided
      if (onAbortSucceeded) {
        onAbortSucceeded();
      }
    },
    [onAbortSucceeded],
  );

  // Define event handlers with useCallback to prevent recreation on each render
  const handleTokenEvent = useCallback(
    (data: TokenEvent) => {
      // Extract text from token event
      let tokenText = '';
      if (data.token && Array.isArray(data.token)) {
        // Handle array of token objects or strings
        tokenText = data.token
          .map((t: string | TokenDelta) => {
            if (typeof t === 'string') return t;
            if (t && typeof t === 'object' && 'input' in t)
              return t.input || '';
            return String(t.text || ''); // Fallback for other cases
          })
          .join('');
      } else if (typeof data.token === 'string') {
        tokenText = data.token;
      } else if (data.token && typeof data.token === 'object') {
        // Handle single token object
        const tokenObj = data.token as TokenDelta;
        tokenText =
          'input' in tokenObj ? tokenObj.input || '' : String(tokenObj);
      }

      // Accumulate raw content (full JSON stream)
      rawContentRef.current += tokenText;

      // Start the smooth rendering interval if not already running
      if (!streamIntervalRef.current) {
        streamIntervalRef.current = setInterval(() => {
          // Extract the full response from raw content
          const extracted = extractResponseFromPartialJson(
            rawContentRef.current,
          );
          const fullExtracted = normalizeMessageBreaksForDisplay(extracted, {
            streaming: true,
          });

          // Extract metadata
          const extractedMetadata = extractMetadataFromPartialJson(
            rawContentRef.current,
          );
          if (
            JSON.stringify(extractedMetadata) !==
            JSON.stringify(streamedMetadataRef.current)
          ) {
            streamedMetadataRef.current = extractedMetadata;
            setStreamedMetadata(extractedMetadata);
          }

          // Check if we need to refresh messages
          if (!extracted && rawContentRef.current.length > 0) {
            setWillNeedToRefreshMessages(true);
          } else {
            setWillNeedToRefreshMessages(false);
          }

          // Incrementally reveal characters for smooth typing effect
          const targetLength = fullExtracted.length;
          if (renderedLengthRef.current > targetLength) {
            renderedLengthRef.current = targetLength;
            setStreamedContent(fullExtracted);
          }

          if (renderedLengthRef.current < targetLength) {
            // Reveal more characters
            renderedLengthRef.current = Math.min(
              renderedLengthRef.current + CHARS_PER_TICK,
              targetLength,
            );
            const visibleContent = fullExtracted.slice(
              0,
              renderedLengthRef.current,
            );
            setStreamedContent(visibleContent);

            if (onScrollNeeded) {
              onScrollNeeded();
            }
          } else if (
            renderedLengthRef.current === targetLength &&
            targetLength > 0
          ) {
            // Caught up - keep interval running for new tokens
            // but update streamedContentRef for final message
            streamedContentRef.current = rawContentRef.current;
          }
        }, TICK_MS);
      }
    },
    [onScrollNeeded],
  );

  const handleToolStart = useCallback(
    (data: ToolStartEvent) => {
      console.log('StreamHandler: Received tool start event:', data);
      setActiveToolCalls((prevCalls: Record<string, ToolStartEvent>) => ({
        ...prevCalls,
        [data.tool]: data,
      }));

      // Add a tool message to the chat
      const toolMessage: AgentMessage = {
        role: 'tool',
        content: data.tool,
        tool_call_input: data.input || undefined,
        tool_call_id: data.id,
      };

      // Add the tool message to the messages array using functional form to avoid stale closures
      setMessages((prevMessages) => [...prevMessages, toolMessage]);

      // Trigger scroll when tool starts
      if (onScrollNeeded) {
        onScrollNeeded();
      }
    },
    [onScrollNeeded, setMessages],
  );

  const handleToolEnd = useCallback((data: ToolEndEvent) => {
    console.log('StreamHandler: Received tool end event:', data);
    setActiveToolCalls((prevCalls: Record<string, ToolStartEvent>) => {
      const newToolCalls = { ...prevCalls };
      delete newToolCalls[data.tool];
      return newToolCalls;
    });
  }, []);

  const handleAgentStreamFinish = useCallback(
    (
      data: AgentStreamFinishEvent & {
        content?: string;
        showToUser?: boolean;
      },
    ) => {
      console.log('StreamHandler: AGENT_STREAM_FINISH event received:', data);

      // Stop the smooth streaming interval
      if (streamIntervalRef.current) {
        clearInterval(streamIntervalRef.current);
        streamIntervalRef.current = null;
      }

      setIsStreaming(false);

      // If we have a message handler, call it with the final streamed content
      // Use rawContentRef which has the complete content
      if (onMessageSent && (rawContentRef.current || data.content)) {
        const hiddenFromUser = data.showToUser === false;

        // Extract the response field from the complete JSON
        let finalContent = hiddenFromUser
          ? ''
          : normalizeMessageBreaksForDisplay(
              extractResponseFromPartialJson(rawContentRef.current),
            );

        // Fallback to server-parsed content if local parsing failed
        // The backend parses the response and sends clean content in the event
        if (!finalContent && !hiddenFromUser && data.content) {
          console.log('StreamHandler: Using server-parsed content as fallback');
          finalContent = normalizeMessageBreaksForDisplay(data.content);
        }

        // Extract metadata from the complete JSON
        const finalMetadata = extractMetadataFromPartialJson(
          rawContentRef.current,
        );

        // Create a message object that matches the expected AgentMessage structure
        const finalMessage: AgentMessage = {
          role: 'agent',
          content: finalContent,
          metadata: finalMetadata,
          timestamp: data.timestamp,
        };

        console.log('StreamHandler: Creating final message:', finalMessage);

        // For existing threads, just add the message
        // For new threads, we'll let the AgentComplete event handle the thread ID update

        // If finalContent is blank then we don't want to show it.
        if (finalContent) {
          // Use a function to get the latest messages state
          setMessages((prevMessages: AgentMessage[]) => [
            ...prevMessages,
            finalMessage,
          ]);
        }
        // Clear the refresh flag - either we added the message or it was intentionally empty (showToUser: false)
        // This prevents the "emergency reload" in handleManagerComplete
        setWillNeedToRefreshMessages(false);
      } else {
        console.warn(
          'StreamHandler: No streamed content available to create final message!',
        );
      }

      // Clear streaming state now that we've used the content
      setIsThinking(false);
      setIsStreaming(false);
      setStreamedContent('');
      streamedContentRef.current = '';
      rawContentRef.current = '';
      renderedLengthRef.current = 0;

      // Clear thinking tokens when stream finishes
      setThinkingTokens('');
    },
    [setMessages],
  );

  // Handle error events from the agent
  const handleAgentError = useCallback(
    (data: AgentErrorEvent) => {
      console.error('StreamHandler: Received agent error event:', data);

      // Cancel any pending stream interval
      if (streamIntervalRef.current) {
        clearInterval(streamIntervalRef.current);
        streamIntervalRef.current = null;
      }

      // Set error state
      setHasError(true);
      setErrorType(data.error.type);

      // Clear streaming state
      setIsThinking(false);
      setIsStreaming(false);
      setStreamedContent('');
      streamedContentRef.current = '';
      rawContentRef.current = '';
      renderedLengthRef.current = 0;

      // Call the onError callback if provided
      if (onError) {
        onError(null);
        // Track cleanup with thread ID and timestamp for safe recovery
        errorCleanupRef.current = {
          threadId: toUseThreadId,
          timestamp: Date.now(),
        };
      }
    },
    [onError, setIsThinking, setIsStreaming, setHasError, toUseThreadId],
  );

  const handleManagerComplete = useCallback(
    // forceRefresh is sent by the backend on the defensive editLastMessage +
    // Phase 1 collision to drop a duplicate message. It is not yet in the
    // published @dreamai/types ManagerCompleteEvent, so extend the type inline.
    (data: ManagerCompleteEvent & { forceRefresh?: boolean }) => {
      console.log('StreamHandler: Received manager complete event:', data);

      // Cancel any pending stream interval
      if (streamIntervalRef.current) {
        clearInterval(streamIntervalRef.current);
        streamIntervalRef.current = null;
      }

      // Reset streaming state
      setIsThinking(false);
      setIsStreaming(false);
      setStreamedContent('');
      streamedContentRef.current = '';
      rawContentRef.current = '';
      renderedLengthRef.current = 0;
      setStreamedMetadata({});
      streamedMetadataRef.current = {};

      // If we need to refresh messages (because we received partial tokens)
      // Read from ref to avoid stale closure when AGENT_STREAM_FINISH and
      // MANAGER_COMPLETE arrive in rapid succession (the state update from
      // handleAgentStreamFinish may not have triggered a re-render yet).
      if (
        (willNeedToRefreshMessagesRef.current || data?.forceRefresh) &&
        fetchThreadMessages &&
        toUseThreadId
      ) {
        console.log('StreamHandler: Refreshing messages after completion');
        fetchThreadMessages(toUseThreadId);
        setWillNeedToRefreshMessages(false);
      }

      // Call onAbortSucceeded to ensure UI state is properly reset
      // This handles the case where an abort was requested but the thread completed
      // before the AGENT_ABORT_SUCCEEDED event was received
      if (onAbortSucceeded) {
        console.log(
          'StreamHandler: Calling onAbortSucceeded from handleAgentComplete',
        );
        onAbortSucceeded();
      }

      // If this is a new conversation, notify parent with the new thread ID and response messages
      // This is critical for streaming new threads - we only update the thread ID when streaming is complete
      if (newThreadId && (!threadId || threadId !== newThreadId)) {
        console.log(
          'StreamHandler: Updating thread ID from streaming:',
          newThreadId,
        );
        if (onMessageSent) {
          setTimeout(() => {
            onMessageSent(newThreadId, []);
          }, 1000);
        }
      }
    },
    [
      threadId,
      onMessageSent,
      newThreadId,
      onAbortSucceeded,
      fetchThreadMessages,
      toUseThreadId,
    ],
  );

  // Register callbacks with ThreadLoadingContext
  useEffect(() => {
    // Create a unique ID for this component instance
    const componentId = `streamHandler-${toUseThreadId || 'new'}`;

    // Register individual agent start/finish callbacks to control thinking state
    const startCallbackId = `${componentId}-individual-agent-start`;
    registerIndividualAgentStartCallback(
      startCallbackId,
      (threadId: string, data: any) => {
        // console.log(`StreamHandler: Individual agent started for thread ${threadId}`);
        if (threadId === toUseThreadId) {
          // console.log('Thinking to true');
          setIsThinking(true);
          setIsStreaming(false);
          setHasError(false);
          setErrorType(null);
          // Clear any previous streamed content and thinking tokens when a new agent starts
          // This is important for auto-abort-then-run: ensures old stream doesn't persist
          setStreamedContent('');
          streamedContentRef.current = '';
          rawContentRef.current = '';
          renderedLengthRef.current = 0;
          setThinkingTokens('');
        }
      },
    );

    const finishCallbackId = `${componentId}-individual-agent-finish`;
    registerIndividualAgentFinishCallback(
      finishCallbackId,
      (threadId: string, data: any) => {
        // console.log(`StreamHandler: Individual agent finished for thread ${threadId}`);
        if (threadId === toUseThreadId) {
          setIsThinking(false);
        }
      },
    );

    // Register agent stream begin callback
    registerAgentStreamBeginCallback(
      componentId,
      (threadId: string, data: AgentStreamBeginEvent) => {
        console.log(
          `StreamHandler: Received agent stream begin callback for thread ${threadId}:`,
          data,
        );
        if (threadId === toUseThreadId) {
          setIsStreaming(true);
          // Clear any error state - agent is actively streaming, so previous errors are stale
          setHasError(false);
          setErrorType(null);

          // If error cleanup was done for THIS thread and within last 60 seconds, recover
          const MAX_RECOVERY_AGE_MS = 60000; // 60 seconds
          if (
            errorCleanupRef.current &&
            errorCleanupRef.current.threadId === threadId &&
            Date.now() - errorCleanupRef.current.timestamp <
              MAX_RECOVERY_AGE_MS &&
            onRecoverFromError
          ) {
            console.log(
              'StreamHandler: Agent recovered from error, calling onRecoverFromError',
              `(error was ${Date.now() - errorCleanupRef.current.timestamp}ms ago)`,
            );
            onRecoverFromError();
            errorCleanupRef.current = null;
          }
        }
      },
    );

    // Register agent stream finish callback
    registerAgentStreamFinishCallback(
      componentId,
      (threadId: string, data: AgentStreamFinishEvent) => {
        console.log(
          `StreamHandler: Received agent stream finish callback for thread ${threadId}:`,
          data,
        );
        if (threadId === toUseThreadId) {
          handleAgentStreamFinish(data);
          setIsStreaming(false);
        }
      },
    );

    // Register manager complete callback
    registerManagerCompleteCallback(
      componentId,
      (threadId: string, data: ManagerCompleteEvent) => {
        console.log(
          `StreamHandler: Received manager complete callback for thread ${threadId}:`,
          data,
        );
        if (threadId === toUseThreadId) {
          handleManagerComplete(data);
          setIsStreaming(false);
        }
      },
    );

    // Register agent error callback
    registerAgentErrorCallback(componentId, (threadId, data) => {
      console.log(
        `StreamHandler: Received agent error callback for thread ${threadId}:`,
        data,
      );
      if (threadId === toUseThreadId) {
        handleAgentError(data);
        setIsStreaming(false);
      }
    });

    // Register abort succeeded callback
    registerAbortSucceededCallback(componentId, (threadId, data) => {
      console.log(
        `StreamHandler: Received abort succeeded callback for thread ${threadId}:`,
        data,
      );
      if (threadId === toUseThreadId) {
        // Reset streaming state
        setStreamedContent('');
        setHasError(false);
        setErrorType(null);
        setIsThinking(false);
        setIsStreaming(false);
        console.log('StreamHandler: Reset streaming state due to abort');
      }
    });

    // Register token callback
    registerTokenCallback(`${componentId}-token`, (threadId, data) => {
      if (threadId === toUseThreadId) {
        handleTokenEvent(data);
      }
    });

    // Register tool start callback
    registerToolStartCallback(`${componentId}-tool-start`, (threadId, data) => {
      console.log(
        `StreamHandler: Received tool start callback for thread ${threadId}:`,
        data,
      );
      if (threadId === toUseThreadId) {
        handleToolStart(data);
      }
    });

    // Register tool end callback
    registerToolEndCallback(`${componentId}-tool-end`, (threadId, data) => {
      console.log(
        `StreamHandler: Received tool end callback for thread ${threadId}:`,
        data,
      );
      if (threadId === toUseThreadId) {
        handleToolEnd(data);
      }
    });

    // Register thinking token callback
    registerThinkingTokenCallback(
      `${componentId}-thinking-token`,
      (threadId, data) => {
        console.log('StreamHandler: Received thinking token:', data);
        if (threadId === toUseThreadId) {
          setThinkingTokens((prev) => prev + (data.content || ''));
        }
      },
    );

    // Register message edited callback
    registerMessageEditedCallback(
      `${componentId}-message-edited`,
      (threadId, data) => {
        console.log('StreamHandler: Received message edited:', data);
        if (threadId === toUseThreadId && data.newContent) {
          // Find and update the last AI message with matching original content
          setMessages((prevMessages: AgentMessage[]) => {
            // First try to find a message matching the original content
            if (data.originalContent) {
              for (let i = prevMessages.length - 1; i >= 0; i--) {
                const msg = prevMessages[i];
                if (
                  msg.role === 'agent' &&
                  msg.content === data.originalContent
                ) {
                  const newMessages = [...prevMessages];
                  newMessages[i] = {
                    ...msg,
                    content: data.newContent,
                    metadata: {
                      ...msg.metadata,
                      editedAt: data.timestamp,
                    },
                  };
                  console.log(
                    'StreamHandler: Updated message at index',
                    i,
                    '(matched by content)',
                  );
                  return newMessages;
                }
              }
            }
            // Fallback: find the last agent message
            for (let i = prevMessages.length - 1; i >= 0; i--) {
              const msg = prevMessages[i];
              if (msg.role === 'agent') {
                const newMessages = [...prevMessages];
                newMessages[i] = {
                  ...msg,
                  content: data.newContent,
                  metadata: {
                    ...msg.metadata,
                    editedAt: data.timestamp,
                  },
                };
                console.log(
                  'StreamHandler: Updated message at index',
                  i,
                  '(fallback to last agent)',
                );
                return newMessages;
              }
            }
            return prevMessages;
          });
        }
      },
    );

    // Cleanup on unmount
    return () => {
      // Clean up individual agent callbacks
      unregisterIndividualAgentStartCallback(startCallbackId);
      unregisterIndividualAgentFinishCallback(finishCallbackId);

      // Unregister callbacks
      unregisterAgentStreamBeginCallback(componentId);
      unregisterAgentStreamFinishCallback(componentId);
      unregisterManagerCompleteCallback(componentId);
      unregisterAgentErrorCallback(componentId);
      unregisterAbortSucceededCallback(componentId);
      unregisterTokenCallback(`${componentId}-token`);
      unregisterThinkingTokenCallback(`${componentId}-thinking-token`);
      unregisterToolStartCallback(`${componentId}-tool-start`);
      unregisterToolEndCallback(`${componentId}-tool-end`);
      unregisterMessageEditedCallback(`${componentId}-message-edited`);
    };
  }, [
    toUseThreadId,
    registerAgentStreamFinishCallback,
    unregisterAgentStreamFinishCallback,
    registerManagerCompleteCallback,
    unregisterManagerCompleteCallback,
    registerAgentErrorCallback,
    unregisterAgentErrorCallback,
    registerAbortSucceededCallback,
    unregisterAbortSucceededCallback,
    registerTokenCallback,
    unregisterTokenCallback,
    registerThinkingTokenCallback,
    unregisterThinkingTokenCallback,
    registerToolStartCallback,
    unregisterToolStartCallback,
    registerToolEndCallback,
    unregisterToolEndCallback,
    handleAgentStreamFinish,
    handleManagerComplete,
    handleAgentError,
    handleTokenEvent,
    handleToolStart,
    handleToolEnd,
    setStreamedContent,
    setHasError,
    setErrorType,
    setIsThinking,
    onRecoverFromError,
    searchParams,
    setSearchParams,
    registerMessageEditedCallback,
    unregisterMessageEditedCallback,
  ]);

  // Set up Pusher subscription when thread ID changes
  useEffect(() => {
    // Verbose logging disabled
    // console.log('StreamHandler: Thread ID effect running with ID:', toUseThreadId);
    // console.log('StreamHandler: Initial streamedContent:', streamedContent);

    if (!toUseThreadId) {
      // console.log('StreamHandler: No threadId provided, skipping Pusher registration');
      return;
    }

    // console.log(`StreamHandler: Setting up TOKEN event handler for thread: ${toUseThreadId}`);

    // Note: ThreadLoadingContext now automatically registers threads from URL and NewThreadContext

    // Set up TOKEN event handler using Pusher directly
    try {
      const channel = subscribeToThreadChannelRef.current(toUseThreadId);
      // console.log('StreamHandler: Received channel from subscribeToThreadChannel:', channel);

      if (!channel) {
        console.error('StreamHandler: Failed to get a valid Pusher channel');
        return;
      }

      threadChannelRef.current = channel;

      // Check if the channel has the bind method
      if (typeof channel.bind !== 'function') {
        console.error(
          'StreamHandler: Channel does not have bind method:',
          channel,
        );
        return;
      }

      console.log('StreamHandler: Setting up event handlers on channel');

      // We no longer need to bind directly to any events
      // ThreadLoadingContext handles all events and calls our registered callbacks

      // We still bind to AGENT_ABORT_SUCCEEDED directly since it's specific to this component
      // and we want immediate feedback in the UI
      channel.bind(
        AgentStreamEvents.AGENT_ABORT_SUCCEEDED,
        handleAbortSucceeded,
      );

      console.log(
        'StreamHandler: Not binding to TOKEN and TOOL events directly',
      );
      console.log('StreamHandler: Using ThreadLoadingContext for all events');

      console.log('StreamHandler: Successfully set up all event handlers');
    } catch (error) {
      console.error(
        'StreamHandler: Error setting up Pusher subscription:',
        error,
      );
    }

    // Cleanup function
    return () => {
      console.log(
        `StreamHandler: Cleaning up Pusher subscription for thread: ${toUseThreadId}`,
      );

      // Unregister the thread from ThreadLoadingContext
      // Note: We don't unregister here to ensure the thread loading state persists
      // even when this component unmounts
      // unregisterThread(toUseThreadId);

      if (threadChannelRef.current) {
        try {
          // Only unbind the AGENT_ABORT_SUCCEEDED event that we bound directly
          if (typeof threadChannelRef.current.unbind === 'function') {
            // We no longer bind to TOKEN, TOOL_START, and TOOL_END events directly
            // So we don't need to unbind them here

            // Unbind the AGENT_ABORT_SUCCEEDED event
            threadChannelRef.current.unbind(
              AgentStreamEvents.AGENT_ABORT_SUCCEEDED,
              handleAbortSucceeded,
            );
            console.log(
              'StreamHandler: Successfully unbound all event handlers',
            );
          } else {
            console.error(
              'StreamHandler: Channel does not have unbind method:',
              threadChannelRef.current,
            );
          }

          // Don't actually unsubscribe from the channel, just set our ref to null
          threadChannelRef.current = null;
        } catch (error) {
          console.error(
            'StreamHandler: Error cleaning up Pusher subscription:',
            error,
          );
        }
      }
    };
  }, [toUseThreadId]);

  // Render error message if there was an error
  if (hasError) {
    if (errorType === 'UNDERLYING_PROVIDER_ISSUE') {
      return (
        <div className='message underlying-provider-error'>
          <div className='message-content'>
            <ErrorHeartbeatIcon />
            <span>
              We’re having an issue with an underlying provider that Dream
              relies on. Trying again may work but you may experience
              intermittent errors while we solve this.
            </span>
          </div>
        </div>
      );
    }
    return (
      <div className='message error'>
        <div className='message-content'>
          <CircleInfoIcon /> Failed to process message. Please try again.
        </div>
      </div>
    );
  }

  // Calculate thinking state and tool description
  const shouldShowThinking = isThinking && (!isStreaming || !streamedContent);

  // Get tool description from active tool call (if any)
  const activeToolKeys = Object.keys(activeToolCalls);
  let toolDescription: string | undefined;
  if (activeToolKeys.length > 0) {
    const activeTool = activeToolCalls[activeToolKeys[0]];
    toolDescription =
      activeTool?.input?.human_understandable_description ||
      `using the ${activeTool?.tool} tool`;
  }

  // Always render ThinkingAnimation (keeps img in DOM), control with visible prop
  const thinkingElement = (
    <div
      className='thinking-wrapper'
      style={
        !shouldShowThinking && !thinkingTokens ? { display: 'none' } : undefined
      }
    >
      <ThinkingAnimation
        agentNickname={agentNickname}
        toolDescription={toolDescription}
        visible={shouldShowThinking}
        thinkingContent={thinkingTokens}
      />
    </div>
  );

  // Render nothing if we're not streaming (but still render hidden thinking element)
  if (!isThinking && !streamedContent) {
    return thinkingElement;
  }

  // If only thinking (no streaming yet), return just the thinking element
  if (shouldShowThinking) {
    return thinkingElement;
  }

  // Format current time for timestamp (matches ThreadView format)
  const currentTimeText = new Date().toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  const messageContent = (
    <div className='message agent-message streaming'>
      {toUseThreadId && (
        <MultiModalContent
          content={streamedContent}
          metadata={streamedMetadata}
          threadId={toUseThreadId}
          colors={undefined}
        />
      )}
      <div className='message-timestamp'>{currentTimeText}</div>
    </div>
  );

  // If a MessageWrapper is provided, use it; otherwise render the message directly
  return MessageWrapper ? (
    <MessageWrapper>{messageContent}</MessageWrapper>
  ) : (
    messageContent
  );
};

// Wrap the component with React.memo to prevent unnecessary re-renders
const StreamHandler = React.memo(
  StreamHandlerComponent,
  (prevProps, nextProps) => {
    // Only re-render if thread ID changes, streaming-related props change, or resetErrorOnNewMessage changes
    return (
      prevProps.threadId === nextProps.threadId &&
      prevProps.onMessageSent === nextProps.onMessageSent &&
      prevProps.onError === nextProps.onError &&
      prevProps.onAbortSucceeded === nextProps.onAbortSucceeded &&
      prevProps.resetErrorOnNewMessage === nextProps.resetErrorOnNewMessage
    );
  },
);

export default StreamHandler;
